import { User } from 'firebase/auth';
import {
  collection, doc, getDoc, getDocs, query, where,
  setDoc, addDoc, updateDoc, deleteDoc, serverTimestamp
} from 'firebase/firestore';
import { limit } from 'firebase/firestore';
import { db, auth, storage } from '../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { ApiResponse, Cliente, ClienteFormData } from '../types';

const userDoc = (uid: string) => doc(db, 'usuarios', uid);
const userCol = (uid: string, sub: 'clientes') => collection(db, 'usuarios', uid, sub);

export const initUser = async (user: User): Promise<ApiResponse<void>> => {
  try {
    // Verificar se já existe um documento
    const docSnap = await getDoc(userDoc(user.uid));
    
    if (!docSnap.exists()) {
      // Criar documento apenas se não existir
      await setDoc(userDoc(user.uid), {
        uid: user.uid,
        email: user.email || '',
        nome: user.displayName || '',
        mensagemRelatorio: '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } else {
      // Se existe, apenas atualizar email (caso tenha mudado)
      const data = docSnap.data();
      if (data.email !== user.email) {
        await updateDoc(userDoc(user.uid), {
          email: user.email || '',
          updatedAt: serverTimestamp(),
        });
      }
    }
    
    return { success: true };
  } catch (e: any) {
    return { success: false, error: e.message };
  }
};

export const isCPFRegistered = async (cpf: string, ignoreId?: string): Promise<boolean> => {
  const u = auth.currentUser;
  if (!u) return false;
  const clean = cpf.replace(/\D/g, '');
  const q = query(userCol(u.uid, 'clientes'), where('cpf', '==', clean));
  const snap = await getDocs(q);
  if (ignoreId) return snap.docs.some(d => d.id !== ignoreId);
  return !snap.empty;
};

export const salvarCliente = async (data: ClienteFormData, idToUpdate?: string): Promise<ApiResponse<string>> => {
  try {
    const u = auth.currentUser;
    if (!u) return { success: false, error: 'Usuário não autenticado' };

    const clean = data.cpf.replace(/\D/g, '');
    const dup = await isCPFRegistered(clean, idToUpdate);
    if (dup) return { success: false, error: 'CPF já cadastrado para este usuário.' };

    // Montar payload base - sem campos undefined
    const payload: any = {
      nome: data.nome,
      telefone: data.telefone,
      cpf: clean,
      medidas: data.medidas || '',
      observacoes: data.observacoes || '',
      userId: u.uid,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    // Adicionar endereco apenas se estiver definido (Firebase não aceita undefined)
    if (data.endereco !== undefined) {
      payload.endereco = data.endereco;
    }

    if (idToUpdate) {
      // Para edição, não incluir createdAt (preservar o original)
      const { createdAt, ...updatePayload } = payload;
      await updateDoc(doc(userCol(u.uid, 'clientes'), idToUpdate), updatePayload);
      clearClientesCache(u.uid);
      return { success: true, id: idToUpdate };
    }

    const ref = await addDoc(userCol(u.uid, 'clientes'), payload);
    clearClientesCache(u.uid);
    return { success: true, id: ref.id };
  } catch (e: any) {
    return { success: false, error: e.message };
  }
};

// Cache manual com sessionStorage (dura apenas a sessão do navegador)
const CACHE_KEY_CLIENTES = 'clientes_cache';
const CACHE_TIMESTAMP = 'clientes_cache_timestamp';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

const getClientesFromCache = (uid: string): Cliente[] | null => {
  try {
    const cachedData = sessionStorage.getItem(`${CACHE_KEY_CLIENTES}_${uid}`);
    const timestamp = sessionStorage.getItem(`${CACHE_TIMESTAMP}_${uid}`);
    
    if (cachedData && timestamp) {
      const age = Date.now() - parseInt(timestamp);
      if (age < CACHE_DURATION) {
        return JSON.parse(cachedData);
      }
    }
  } catch (e) {
    // Silently handle cache errors
  }
  return null;
};

const setClientesInCache = (uid: string, data: Cliente[]) => {
  try {
    sessionStorage.setItem(`${CACHE_KEY_CLIENTES}_${uid}`, JSON.stringify(data));
    sessionStorage.setItem(`${CACHE_TIMESTAMP}_${uid}`, Date.now().toString());
  } catch (e) {
    // Silently handle cache errors
  }
};

const clearClientesCache = (uid: string) => {
  try {
    sessionStorage.removeItem(`${CACHE_KEY_CLIENTES}_${uid}`);
    sessionStorage.removeItem(`${CACHE_TIMESTAMP}_${uid}`);
  } catch (e) {
    // Silently handle cache errors
  }
};

// Função para buscar clientes com filtro opcional e cache manual
export const getClientesByUser = async (searchTerm?: string): Promise<ApiResponse<Cliente[]>> => {
  try {
    const u = auth.currentUser;
    if (!u) return { success: false, error: 'Usuário não autenticado' };
    
    let clientes: Cliente[] = [];
    
    // Tentar buscar do cache primeiro
    const cachedClientes = getClientesFromCache(u.uid);
    
    if (cachedClientes) {
      clientes = cachedClientes;
    } else {
      // Buscar do servidor se não tiver no cache
      const snap = await getDocs(userCol(u.uid, 'clientes'));
      clientes = snap.docs.map(d => ({ id: d.id, ...d.data() } as Cliente));
      
      // Salvar no cache
      setClientesInCache(u.uid, clientes);
    }
    
    let filteredClientes = clientes;
    
    // Se houver termo de busca, filtrar localmente
    if (searchTerm && searchTerm.trim()) {
      const termo = searchTerm.toLowerCase();
      filteredClientes = clientes.filter(cliente => {
        const nome = (cliente.nome || '').toLowerCase();
        const telefone = (cliente.telefone || '').toLowerCase();
        const cpf = cliente.cpf || '';
        
        return nome.includes(termo) || 
               telefone.includes(termo) || 
               cpf.includes(termo);
      });
      
    }
    
    return { success: true, data: filteredClientes };
  } catch (e: any) {
    return { success: false, error: e.message };
  }
};

export const deleteClienteByUser = async (id: string): Promise<ApiResponse<void>> => {
  try {
    const u = auth.currentUser;
    if (!u) return { success: false, error: 'Usuário não autenticado' };
    await deleteDoc(doc(userCol(u.uid, 'clientes'), id));
    clearClientesCache(u.uid);
    return { success: true };
  } catch (e: any) {
    return { success: false, error: e.message };
  }
};

export const getUserProfile = async (): Promise<ApiResponse<{ nome: string; email: string; photoURL?: string; createdAt?: Date; mensagemRelatorio?: string; contractLogoURL?: string }>> => {
  try {
    const user = auth.currentUser;
    if (!user) return { success: false, error: 'Usuário não autenticado' };

    const snap = await getDoc(doc(db, 'usuarios', user.uid));
    if (!snap.exists()) {
      // Se o documento não existe, criar um com os dados do Firebase Auth
      await setDoc(doc(db, 'usuarios', user.uid), {
        uid: user.uid,
        email: user.email || '',
        nome: user.displayName || '',
        mensagemRelatorio: '',
        contractLogoURL: '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      
      return {
        success: true,
        data: {
          nome: user.displayName || '',
          email: user.email || '',
          photoURL: user.photoURL || '',
          createdAt: user.metadata.creationTime ? new Date(user.metadata.creationTime) : new Date(),
        mensagemRelatorio: '',
        contractLogoURL: ''
      }
    };
  }

  const data = snap.data() as any;
  return {
    success: true,
    data: {
      nome: data.nome || '', // Sempre usar o nome do Firestore, não do Auth
      email: data.email || user.email || '',
      photoURL: data.photoURL || user.photoURL || '',
      createdAt: data.createdAt?.toDate() || (user.metadata.creationTime ? new Date(user.metadata.creationTime) : new Date()),
      mensagemRelatorio: data.mensagemRelatorio || '',
      contractLogoURL: data.contractLogoURL || ''
    }
  };
} catch (e: any) {
  return { success: false, error: e.message };
}
};

export const updateUserProfile = async (nome: string): Promise<ApiResponse<void>> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return { success: false, error: 'Usuário não autenticado' };
    }

    // Verificar se o documento existe primeiro
    const userDocRef = userDoc(user.uid);
    const docSnap = await getDoc(userDocRef);
    
    if (!docSnap.exists()) {
      // Criar o documento se não existir
      await setDoc(userDocRef, {
        uid: user.uid,
        email: user.email || '',
        nome: nome.trim(),
        mensagemRelatorio: '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } else {
      // IMPORTANTE: Atualizar APENAS o nome e updatedAt
      // NUNCA tocar no createdAt para preservar a data de criação original
      await updateDoc(userDocRef, {
        nome: nome.trim(),
        updatedAt: serverTimestamp(),
      });
    }

    return { success: true };
  } catch (e: any) {
    return { success: false, error: e.message };
  }
};

export const updateUserReportMessage = async (mensagem: string): Promise<ApiResponse<void>> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return { success: false, error: 'Usuário não autenticado' };
    }

    const userDocRef = userDoc(user.uid);
    const docSnap = await getDoc(userDocRef);

    if (!docSnap.exists()) {
      await setDoc(userDocRef, {
        uid: user.uid,
        email: user.email || '',
        nome: user.displayName || '',
        mensagemRelatorio: mensagem.slice(0, 1300),
        contractLogoURL: '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } else {
      await updateDoc(userDocRef, {
        mensagemRelatorio: mensagem.slice(0, 1300),
        updatedAt: serverTimestamp(),
      });
    }

    return { success: true };
  } catch (e: any) {
    return { success: false, error: e.message };
  }
};

// Função para buscar cliente por ID (pública)
export const getClienteById = async (id: string): Promise<ApiResponse<Cliente>> => {
  try {
    const { doc, getDoc } = await import('firebase/firestore');
    const { db } = await import('../firebase/config');
    
    const clienteDoc = await getDoc(doc(db, 'clientes', id));
    
    if (!clienteDoc.exists()) {
      return { success: false, error: 'Cliente não encontrado' };
    }

    const cliente = { id: clienteDoc.id, ...clienteDoc.data() } as Cliente;
    return { success: true, data: cliente };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const updateUserContractLogo = async (file: File): Promise<ApiResponse<string>> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return { success: false, error: 'Usuário não autenticado' };
    }

    if (!file.type.startsWith('image/')) {
      return { success: false, error: 'Arquivo inválido. Envie uma imagem.' };
    }

    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      return { success: false, error: 'Imagem excede 2MB' };
    }

    const path = `usuarios/${user.uid}/contract_logo/logo_${Date.now()}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);

    const userDocRef = userDoc(user.uid);
    const docSnap = await getDoc(userDocRef);

    if (!docSnap.exists()) {
      await setDoc(userDocRef, {
        uid: user.uid,
        email: user.email || '',
        nome: user.displayName || '',
        mensagemRelatorio: '',
        contractLogoURL: url,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } else {
      await updateDoc(userDocRef, {
        contractLogoURL: url,
        updatedAt: serverTimestamp(),
      });
    }

    return { success: true, data: url };
  } catch (e: any) {
    return { success: false, error: e.message };
  }
};
